import React, { useState } from 'react';

import { Button, Group, Text, TextInput } from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import KiloActionIcon from 'components/KiloActionIcon';
import useUpdateFlow from 'hooks/flows/useUpdateFlow';
import { Flow } from 'types';
import notificationHandler from 'utils/notifications';

const FlowNameCell = ({ flow, onOpenFlow }: { flow: Flow; onOpenFlow: () => void }): JSX.Element => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(flow.name ?? '');
  const { mutate, isLoading } = useUpdateFlow();
  const { successNotification, errorNotification } = notificationHandler();

  const startEditing = (): void => {
    setName(flow.name ?? '');
    setIsEditing(true);
  };

  const cancelEditing = (): void => {
    setName(flow.name ?? '');
    setIsEditing(false);
  };

  const handleSave = (): void => {
    const trimmed = name.trim();
    if (trimmed === (flow.name ?? '')) {
      setIsEditing(false);
      return;
    }
    mutate(
      {
        ...flow,
        name: trimmed || null,
        notificationMessageTemplate: flow.notificationMessageTemplate.notificationMessageTemplateId,
      },
      {
        onSuccess: () => {
          successNotification('Flow renamed', 'Flow name has been updated successfully');
          setIsEditing(false);
        },
        onError: error => {
          errorNotification('Flow rename failed', error.response?.data.message);
        },
      },
    );
  };

  if (isEditing) {
    return (
      <Group spacing='xs' noWrap>
        <TextInput
          autoFocus
          value={name}
          placeholder={flow.trigger}
          maxLength={255}
          disabled={isLoading}
          onChange={event => setName(event.currentTarget.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              handleSave();
            } else if (event.key === 'Escape') {
              cancelEditing();
            }
          }}
          data-cy='flowNameInlineInput'
        />
        <Button size='xs' compact onClick={handleSave} loading={isLoading} data-cy='flowNameSaveButton'>
          Save
        </Button>
        <Button
          size='xs'
          compact
          variant='subtle'
          onClick={cancelEditing}
          disabled={isLoading}
          data-cy='flowNameCancelButton'
        >
          Cancel
        </Button>
      </Group>
    );
  }

  return (
    <Group
      spacing='xs'
      noWrap
      sx={{
        '[data-flow-name-edit]': { opacity: 0, transition: 'opacity 120ms ease' },
        '&:hover [data-flow-name-edit]': { opacity: 1 },
      }}
    >
      <Text onClick={onOpenFlow} weight='bold' underline sx={{ cursor: 'pointer', width: 'fit-content' }}>
        {flow.name || flow.trigger}
      </Text>
      <KiloActionIcon
        aria-label='Edit flow name'
        onClick={startEditing}
        data-cy='flowNameEditButton'
        data-flow-name-edit=''
        color='gray'
        variant='subtle'
      >
        <IconPencil size={16} color='gray' />
      </KiloActionIcon>
    </Group>
  );
};

export default FlowNameCell;
